<template>
  <div
    :id="id"
    :class="$style.wrapper"
    :style="{
      border: isOfferSelected ? '2px solid var(--vd-color-blue-default)' : '1px solid #dedee0',
    }"
  >
    <Collapsor v-model="isContentVisibleLocal">
      <template #selection>
        <CheckBox v-model="isOfferSelected" :binary="true" @click="(event) => event.stopPropagation()" />
        <p :class="$style.idWrapper">
          ID:
          <span :class="$style.idText">{{ id }}</span>
        </p>
      </template>
      <template #action>
        <OfferABTestChip
          v-if="flags.offerMultiLinkABTest.enabled"
          :ab-test-id="abTestId"
          :abtest-link-mode="abtestLinkMode"
          :abtest-links="abtestLinks"
        />
        <i :class="[isContentVisibleLocal ? 'pi pi-chevron-up' : 'pi pi-chevron-down', $style.toggleIcon]"></i>
        <OfferMenuDropdown
          v-if="isWipVersion"
          :status="status"
          :ab-test-id="abTestId"
          :abtest-links="abtestLinks"
          :use-multi-link="flags.offerMultiLinkABTest.enabled"
          :is-offer-ready="isOfferReady"
          @duplicate="handleOfferDuplicate"
          @stop-offer="handleOfferTermination"
          @mark-as-ready="handleMarkAsReady"
          @mark-as-draft="handleMarkAsDraft"
          @cancel-stopping-the-offer="handleCancelStoppingOffer"
          @delete="handleOfferDelete"
        />
      </template>
      <template #header>
        <div :class="$style.header">
          <div :class="$style.headerLeftSection">
            <OfferByStatus :status="status" :ab-test-id="flags.offerMultiLinkABTest.enabled ? null : abTestId" />
            <OfferName
              :name="name"
              :technical-name="technicalName"
              :is-editable="isOfferEditable"
              @update:name="handleNameUpdate"
            />
          </div>
          <div :class="$style.headerRightSection">
            <OfferCampaign
              :game-id="gameId"
              :version-id="versionId"
              :campaign-id="campaignId"
              :is-editable="isOfferEditable"
              @update:selected-campaign="handleUpdateOfferCampaign"
            />
            <OfferDateRange
              :start-date="startDate"
              :end-date="endDate"
              :offer-status="status"
              :is-editable="isOfferEditable"
              @update-dates="handleUpdateDates"
            />
            <OfferTimerDropDown
              :display-count-down="displayCountDown"
              :editable="isOfferEditable"
              :timer-duration="timer.timerDuration"
              :timer-type="timer.timerType"
              :start-date="startDate"
              :end-date="endDate"
              @update="handleOfferTimerUpdate"
            />
          </div>
        </div>
      </template>
      <Tabs value="0">
        <TabList>
          <Tab value="0">Parameters</Tab>
          <Tab value="1">Pricing + rewards</Tab>
          <Tab value="2">Display</Tab>
          <Tab v-if="flags.offerMultiLinkABTest.enabled" value="3">
            ABTest
            <span :class="$style.newBadge">NEW</span>
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="0">
            <div :class="$style.content">
              <OfferParametersTab
                :conditions="sanitizedConditions"
                :tags="tags"
                :triggers="triggers"
                :is-offer-editable="isOfferEditable"
                @update="handleOfferConditionUpdate"
                @update:triggers="handleTriggerSelection"
                @update:tags="handleUpdateTags"
              />
            </div>
          </TabPanel>
          <TabPanel value="1">
            <div :class="$style.content">
              <OfferRewardsTab
                :offer-id="id"
                :is-editable="isOfferEditable"
                :type="type"
                :is-chained="isChained"
                :is-endless="isEndless"
                :offer-buy-all-details="offerBuyAllDetails"
                @is-offer-ready="handleIsOfferReady"
                @offer-product-type-update="handleOfferProductTypeUpdate"
                @offer-chained-property-update="handleIsChainedOfferUpdate"
                @offer-endless-property-update="handleIsEndlessOfferUpdate"
                @update-buy-all-details="(details) => emit('update-buy-all-details', details)"
              />
            </div>
          </TabPanel>
          <TabPanel value="2">
            <div :class="$style.content">
              <OfferDisplayTab
                :image="image"
                :menu-badge="menuBadge"
                :title="title"
                :is-wip-version="isWipVersion"
                :status="status"
                :highlight-texts="highlightTexts"
                :display-count-down="displayCountDown"
                @update-menu-badge="handleOfferMenuBadgeUpdate"
                @update-image="handleOfferImageUpdate"
                @update-title="handleTitleUpdate"
                @update-highlight-texts="handleHighlightTextsUpdate"
                @update-display-countdown-in-menu-badge="handleDisplayCountdownInMenuBadgeUpdate"
              />
            </div>
          </TabPanel>
          <TabPanel v-if="flags.offerMultiLinkABTest.enabled" value="3">
            <div :class="$style.content">
              <OfferABTestTab
                :ab-test-id="abTestId"
                :abtest-link-mode="abtestLinkMode"
                :abtest-links="abtestLinks"
                @update-abtest-link-mode="(mode) => emit('updateAbtestLinkMode', mode)"
              />
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <div :class="$style.actionRow">
        <Button
          :disabled="!canBeMarkedAsDraft || !isOfferReady"
          label="Mark as ready"
          outlined
          icon="pi pi-check"
          @click="handleMarkAsReady"
        />
      </div>
    </Collapsor>
  </div>
</template>

<script setup>
import OfferCampaign from "@/modules/Offers/components/OfferCampaign.vue";
import Button from "primevue/button";
import CheckBox from "primevue/checkbox";
import Tab from "primevue/tab";
import TabList from "primevue/tablist";
import TabPanel from "primevue/tabpanel";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { OfferType } from "@/graphql";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import OfferDisplayTab from "./OfferDisplayTab/OfferDisplayTab.vue";
import OfferParametersTab from "./OfferParametersTab/OfferParametersTab.vue";
import OfferRewardsTab from "./OfferRewardsTab/OfferRewardsTab.vue";
import Collapsor from "./components/Collapsor.vue";
import OfferABTestChip from "./components/OfferABTestChip.vue";
import OfferABTestTab from "./components/OfferABTestTab.vue";
import OfferByStatus from "./components/OfferByStatus.vue";
import OfferDateRange from "./components/OfferDateRange.vue";
import OfferMenuDropdown from "./components/OfferMenuDropdown.vue";
import OfferName from "./components/OfferName.vue";
import OfferTimerDropDown from "./components/OfferTimerDropDown.vue";

const route = useRoute();
const gameId = ref(route.params.gameId);
const versionId = ref(route.params.versionId);

// Gates the multi-link A/B-test UI (ABTest tab + Included/Excluded cohort pill). When off, the offer shows
// the legacy "AB | Status" pill and no ABTest tab.
const { flags } = useFeatureFlags(gameId.value);

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  highlightTexts: {
    type: Array,
  },
  technicalName: {
    type: String,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  startDate: {
    type: String,
    required: true,
  },
  endDate: {
    type: String,
    required: true,
  },
  conditions: {
    type: Object,
    default: () => ({}),
  },
  triggers: {
    type: Array,
  },
  tags: {
    type: Array,
  },
  androidIap: Object,
  iosIap: Object,
  isContentVisible: Boolean,
  timer: Object,
  displayCountDown: Boolean,
  menuBadge: Object,
  image: {
    type: String,
  },
  isWipVersion: {
    type: Boolean,
    required: true,
  },
  abTestId: {
    type: String,
  },
  abtestLinkMode: {
    type: String,
    default: null,
  },
  abtestLinks: {
    type: Array,
    default: () => [],
  },
  campaignId: {
    type: String,
  },
  type: {
    type: String,
    required: false,
    default: OfferType.OnePo,
  },
  isEndless: {
    type: Boolean,
    required: true,
  },
  isChained: {
    type: Boolean,
    required: true,
  },
  isSelected: {
    type: Boolean,
    required: true,
  },
  offerBuyAllDetails: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  "updateTimer",
  "updateConditions",
  "duplicate",
  "updateInAppPurchase",
  "updateDates",
  "updateMenuBadge",
  "updateImage",
  "selectOffer",
  "stopOffer",
  "markAsReady",
  "deleteOffer",
  "markAsDraft",
  "cancelStoppingOffer",
  "updateName",
  "updateTitle",
  "updateSelectedTriggers",
  "updateTags",
  "updateDiscountInPercent",
  "updateHighlightTexts",
  "updateDisplayCountdownInMenuBadge",
  "updateOfferCampaign",
  "updateOfferType",
  "update-offer-is-chained",
  "update-offer-is-endless",
  "update-buy-all-details",
  "updateAbtestLinkMode",
]);

const isOfferSelected = computed({
  get() {
    return props.isSelected;
  },
  set(newValue) {
    emit("selectOffer", newValue);
  },
});
const isOfferReady = ref(false);
const isContentVisibleLocal = ref(false);
const canBeMarkedAsDraft = computed(() => props.isWipVersion && props.status === "DRAFT");
watch(
  () => props.isContentVisible,
  (newValue) => {
    isContentVisibleLocal.value = newValue;
  }
);

const isOfferEditable = computed(() => props.status === "DRAFT" && props.isWipVersion);
const sanitizedConditions = computed(() => ({
  activeTime: props.conditions.activeTime
    ? {
        from: props.conditions.activeTime.from,
        to: props.conditions.activeTime.to,
      }
    : null,

  activeDays: props.conditions.activeDays,
  coolDown: props.conditions.coolDown
    ? {
        days: props.conditions.coolDown.days,
        hours: props.conditions.coolDown.hours,
      }
    : null,
  dailyLimit: props.conditions.dailyLimit,
  rewardMultiplier: props.conditions.rewardMultiplier,
  skipFrequency: props.conditions.skipFrequency,
  segments: props.conditions.segments,
  discountInPercent: props.conditions.discountInPercent,
}));

const hadAnythingChanged = (newItem, oldItem) => JSON.stringify(newItem) !== JSON.stringify(oldItem);

const handleOfferConditionUpdate = (conditions) => {
  if (hadAnythingChanged(conditions, sanitizedConditions.value)) {
    emit("updateConditions", conditions);
  }
};

const handleOfferTimerUpdate = (timerProps) => {
  const oldValues = {
    displayCountDown: props.displayCountDown,
    timerType: props.timer?.timerType,
    timerDuration: props.timer?.timerDuration,
  };
  if (hadAnythingChanged(timerProps, oldValues)) {
    emit("updateTimer", timerProps);
    const isCountDownShownInBadge =
      props.menuBadge?.displayCountDownInMenuBadge !== undefined ? props.menuBadge?.displayCountDownInMenuBadge : false;
    if (isCountDownShownInBadge && !props.displayCountDown) {
      handleDisplayCountdownInMenuBadgeUpdate(false);
    }
  }
};

function handleDisplayCountdownInMenuBadgeUpdate(displayCountDownInMenuBadge) {
  emit("updateDisplayCountdownInMenuBadge", displayCountDownInMenuBadge);
}

function handleNameUpdate(newName) {
  emit("updateName", newName);
}

function handleTitleUpdate(newTitle) {
  emit("updateTitle", newTitle);
}

function handleHighlightTextsUpdate(newTexts) {
  emit("updateHighlightTexts", newTexts);
}

function handleOfferMenuBadgeUpdate(menuBadge) {
  const oldValues = {
    displayCountDownInMenuBadge: props.menuBadge?.displayCountDownInMenuBadge,
    displayMenuBadge: props.menuBadge?.displayMenuBadge,
    menuBadgeIcon: props.menuBadge?.menuBadgeIcon,
  };
  if (hadAnythingChanged(menuBadge, oldValues)) {
    emit("updateMenuBadge", menuBadge);
  }
}

function handleOfferImageUpdate(image) {
  emit("updateImage", image);
}

const handleOfferDuplicate = () => {
  emit("duplicate");
};

const handleOfferTermination = () => {
  emit("stopOffer");
};

const handleUpdateDates = (dates) => {
  emit("updateDates", dates);
};

function handleMarkAsReady() {
  emit("markAsReady");
}

function handleMarkAsDraft() {
  emit("markAsDraft");
}
function handleCancelStoppingOffer() {
  emit("cancelStoppingOffer");
}

function handleOfferDelete() {
  emit("deleteOffer");
}

const handleTriggerSelection = (updatedTriggers) => {
  emit("updateSelectedTriggers", updatedTriggers);
};

const handleUpdateTags = (updatedTags) => {
  emit("updateTags", updatedTags);
};

const handleUpdateOfferCampaign = (selectedCampaign) => {
  emit("updateOfferCampaign", selectedCampaign);
};

const handleIsOfferReady = (value) => {
  isOfferReady.value = value;
};

const handleOfferProductTypeUpdate = (newType) => {
  if (props.type !== newType) {
    emit("updateOfferType", newType);
  }
};

const handleIsChainedOfferUpdate = (value) => {
  if (props.isChained !== value) {
    emit("update-offer-is-chained", value);
  }
};

const handleIsEndlessOfferUpdate = (value) => {
  if (props.isEndless !== value) {
    emit("update-offer-is-endless", value);
  }
};

onMounted(() => {
  isContentVisibleLocal.value = props.isContentVisible;

  const permalinkId = route.hash.slice(1);
  if (permalinkId === props.id) {
    isContentVisibleLocal.value = true;
    nextTick(() => {
      document.getElementById(props.id)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }
});
</script>

<style module>
.wrapper {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: inherit;
  font-family: "Arial", sans-serif;
  background-color: var(--vd-color-white);
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  gap: 10px;
}

.headerLeftSection {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-grow: 1;
}

.headerRightSection {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.toggleIcon {
  cursor: pointer;
  color: var(--vd-color-text-light-2);
  font-size: 1rem;
}

.newBadge {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 8px;
  background-color: var(--vd-color-blue-default, #2563eb);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  vertical-align: middle;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  flex: 1;
  padding: 0 1rem;
}

.actionRow {
  display: flex;
  justify-content: flex-end;
  padding: 8px;
}

.idText {
  color: var(--vd-color-text-light-2);
  font-size: 14px;
  line-height: 14px;
  font-weight: 400;
}
.idWrapper {
  font-weight: 700;
  font-size: 14px;
}
</style>
